import { m } from "@recobra/i18n/messages";
import type { NotificationInboxItem, NotificationKind } from "@recobra/schemas/notification";

import type { TaskName } from "../tasks";
import {
  formatCaseStepNotification,
  formatDocumentNotification,
  formatDocumentUploadedNotification,
  formatMessageNotification,
  formatNoteNotification,
  formatSecurityNotification,
} from "./formatDomainNotificationContent";
import { formatTaskNotification } from "./formatTaskNotificationContent";

export type FormattedNotificationContent = {
  title: string;
  message: string;
  actionLabel: string | undefined;
};

const defaultActionLabel = (item: NotificationInboxItem): string | undefined => {
  if (item.action?.label) return item.action.label;

  switch (item.link.type) {
    case "task":
      return m.notifications_action_view_task();
    case "case":
      return m.notifications_action_view_case();
    case "client":
      return m.notifications_action_view_client();
    case "conversation":
      return m.notifications_action_view_conversation();
    case "url":
      return m.notifications_action_open();
    case "notifications":
      return m.notifications_action_view_notifications();
  }
};

const storedContent = (item: NotificationInboxItem): FormattedNotificationContent => ({
  title: item.title,
  message: item.bodyExpanded ?? item.body,
  actionLabel: defaultActionLabel(item),
});

const formatByKind = (
  kind: NotificationKind,
  item: NotificationInboxItem,
  taskName: TaskName,
): FormattedNotificationContent | null => {
  switch (kind) {
    case "task.assigned":
    case "task.canceled":
    case "task.reopened":
    case "task.completed":
    case "task.sla_breach":
    case "task.reminder":
      return formatTaskNotification(item, kind, taskName);
    case "note.added":
    case "note.mention":
      return formatNoteNotification(item, kind);
    case "case.step_changed":
      return formatCaseStepNotification(item);
    case "document.classified":
      return formatDocumentNotification(item);
    case "document.uploaded":
      return formatDocumentUploadedNotification();
    case "message.received":
      return formatMessageNotification(item);
    case "security.sign_in":
      return formatSecurityNotification(item, defaultActionLabel(item));
    case "system.broadcast":
      return storedContent(item);
    default:
      return null;
  }
};

export const formatNotificationContent = (
  item: NotificationInboxItem,
  taskName: TaskName,
): FormattedNotificationContent => {
  const localized = formatByKind(item.kind, item, taskName);
  if (localized) return localized;

  return storedContent(item);
};
